import {
  ClockIcon,
  DocumentIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  TruckIcon,
  UserIcon,
  CalendarIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";
import { getAppType } from "@/utils";
import { BookForm } from "@/components/BookForm";
import { AssignedToSelect } from "@/components";
import { AssignUserForm } from "@/components/AssignUserForm";
import StartJobForm from "@/components/StartJobForm";
import { getStatusConfigs } from "@/utils/status-configs";

interface StageFormProps {
  assignedTo: string[];
  [key: string]: any;
}

interface Stage {
  order: number;
  id: string;
  title: string;
  next?: string | ((options: { form: StageFormProps }) => string);
  button?: string;
  modalTitle?: string;
  icon: React.ComponentType;
  modal?: React.ComponentType<any>;
  color?: "orange" | "blue" | "yellow" | "cyan" | "gray" | "green" | "purple";
}

interface JobStages {
  [key: string]: {
    [key: number]: Stage;
  };
}

interface StageColors {
  text: {
    [key: string]: string;
  };
  bg: {
    [key: string]: string;
  };
  border: {
    [key: string]: string;
  };
}

export const jobStages: JobStages = {
  trucking: {
    1: {
      order: 1,
      id: "new",
      title: "New",
      next: "scheduled",
      button: "Schedule",
      icon: DocumentTextIcon,
    },
    2: {
      order: 2,
      id: "scheduled",
      title: "Scheduled",
      next: "dispatched",
      button: "Dispatch",
      color: "orange",
      icon: ClockIcon,
    },
    3: {
      order: 3,
      id: "dispatched",
      title: "Dispatched",
      next: "in_progress",
      button: "Start Load",
      color: "yellow",
      icon: TruckIcon,
    },
    4: {
      order: 4,
      id: "in_progress",
      title: "In Progress",
      next: "completed",
      color: "cyan",
      icon: WrenchIcon,
    },
    5: {
      order: 5,
      id: "completed",
      title: "Completed",
      next: "invoiced",
      color: "green",
      icon: CheckCircleIcon,
    },
    6: {
      order: 6,
      id: "invoiced",
      title: "Invoiced",
      next: "paid",
      color: "purple",
      icon: DocumentIcon,
    },
    7: {
      order: 7,
      id: "paid",
      title: "Paid",
      color: "green",
      icon: CheckCircleIcon,
    },
  },
  serviceRepair: {
    1: {
      order: 1,
      id: "new",
      title: "New",
      next: "pending",
      button: "Publish",
      modalTitle: "Publish Job",
      icon: DocumentTextIcon,
    },
    2: {
      order: 2,
      id: "pending",
      title: "Pending",
      next: ({ form }) => {
        return form.assignedTo.length > 0 ? "assigned" : "booked";
      },
      button: "Book",
      icon: ClockIcon,
      modal: (props) => <BookForm {...props} />,
      modalTitle: "Book Job",
      color: "orange",
    },
    3: {
      order: 3,
      id: "booked",
      title: "Booked",
      next: "assigned",
      button: "Assign",
      icon: CalendarIcon,
      modal: (props) => <AssignUserForm {...props} />,
      modalTitle: "Assign Job",
      color: "blue",
    },
    4: {
      order: 4,
      id: "assigned",
      title: "Assigned",
      next: "inProgress",
      button: "Start Job",
      modal: (props) => <StartJobForm {...props} />,
      icon: UserIcon,
      color: "yellow",
    },
    5: {
      order: 5,
      id: "inProgress",
      title: "In Progress",
      next: "completed",
      button: "Mark Completed",
      icon: WrenchIcon,
      modalTitle: "Mark Job as Completed",
      color: "cyan",
    },
    6: {
      order: 6,
      id: "completed",
      title: "Completed",
      next: "invoiced",
      icon: CheckCircleIcon,
      modalTitle: "Create Invoice",
      color: "green",
    },
    7: {
      order: 7,
      id: "invoiced",
      title: "Invoiced",
      icon: DocumentIcon,
      color: "purple",
    },
  },
};

let warnedStatusMismatch = false;

const mergeStageMeta = (
  localStages: { [key: number]: Stage },
  remoteStages: any,
  type: string
) => {
  if (!remoteStages) return localStages;

  const merged: { [key: number]: Stage } = {};
  Object.keys(localStages).forEach((key) => {
    const localStage = localStages[Number(key)];
    const match = (Object.values(remoteStages) as any[]).find((remote: any) => {
      return (
        normalizeJobStatusForStages(remote?.id, type) ===
        normalizeJobStatusForStages(localStage.id, type)
      );
    }) as any;

    merged[Number(key)] = {
      ...localStage,
      title: match?.title || localStage.title,
      color: match?.color || localStage.color,
    };
  });

  if (!warnedStatusMismatch && process.env.NODE_ENV === "development") {
    const localIds = Object.values(localStages).map((s) =>
      normalizeJobStatusForStages(s.id, type)
    );
    const remoteIds = Object.values(remoteStages).map((s: any) =>
      normalizeJobStatusForStages(s.id, type)
    );

    const missingInRemote = localIds.filter((id) => !remoteIds.includes(id));
    const extraInRemote = remoteIds.filter((id) => !localIds.includes(id));

    if (missingInRemote.length || extraInRemote.length) {
      // eslint-disable-next-line no-console
      console.warn("[StatusConfigs] job stage mismatch", {
        missingInRemote,
        extraInRemote,
      });
    }
    warnedStatusMismatch = true;
  }

  return merged;
};

export const stageColors: StageColors = {
  text: {
    orange: "text-orange-500",
    blue: "text-blue-500",
    yellow: "text-yellow-500",
    cyan: "text-cyan-500",
    gray: "text-gray-500",
    green: "text-green-500",
    purple: "text-purple-500",
  },
  bg: {
    orange: "bg-orange-100",
    blue: "bg-blue-100",
    yellow: "bg-yellow-100",
    cyan: "bg-cyan-100",
    gray: "bg-gray-100",
    green: "bg-green-100",
    purple: "bg-purple-100",
  },
  border: {
    orange: "border-orange-300",
    blue: "border-blue-300",
    yellow: "border-yellow-300",
    cyan: "border-cyan-300",
    gray: "border-gray-300",
    green: "border-green-300",
    purple: "border-purple-300",
  },
};

export const getStages = (
  type: string = getAppType()
): { [key: number]: Stage } => {
  const remote =
    getStatusConfigs()?.jobStages?.[type] ||
    getStatusConfigs()?.jobStages?.serviceRepair;
  const localStages = jobStages[type] || jobStages.serviceRepair;
  return mergeStageMeta(localStages, remote, type);
};

export const getStage = (
  key: string | number,
  type: string = getAppType()
): Stage => {
  let res: Stage = {} as Stage;
  if (typeof key === "string") {
    const normalizedKey = normalizeJobStatusForStages(key, type);
    const stages = getStages(type);
    const current = Object.keys(stages).find(
      (i) =>
        stages[i].id === normalizedKey ||
        stages[i].title === key ||
        stages[i].title === normalizedKey
    );
    res = current ? stages[current] : ({} as Stage);
  } else {
    const stages = getStages(type);
    res = stages[key] || ({} as Stage);
  }
  return res;
};

/**
 * Normalize incoming job.status to a stage id for the given app type.
 * This keeps the UI backward compatible with legacy stored values like "En Route".
 */
export const normalizeJobStatusForStages = (
  status: string | undefined | null,
  type: string = getAppType()
): string => {
  const raw = String(status || "");
  const normalized = raw.toLowerCase().replace(/[_\s-]/g, "");

  if (type === "trucking") {
    const map: Record<string, string> = {
      // canonical
      new: "new",
      scheduled: "scheduled",
      dispatched: "dispatched",
      inprogress: "in_progress",
      in_progress: "in_progress",
      completed: "completed",
      invoiced: "invoiced",
      paid: "paid",

      // legacy
      pending: "scheduled",
      enroute: "in_progress",
      delivered: "completed",
      "in-progress": "in_progress",
    };

    return map[normalized] || "new";
  }

  // serviceRepair stages use camelCase ids (inProgress). Keep existing behavior.
  return normalized === "inprogress" ? "inProgress" : raw;
};

interface NextStageOptions {
  status?: string;
  form?: StageFormProps;
  [key: string]: any;
}

export const getNextStage = (
  key: string | number,
  options: NextStageOptions = {},
  type: string = getAppType()
): Stage | null => {
  const stages = getStages(type);
  if (typeof key === "string") {
    const normalizedKey = normalizeJobStatusForStages(key, type);
    const current = Object.keys(stages).find(
      (i) => stages[i].id === normalizedKey
    );
    if (current && !stages[current].next) {
      return null;
    }
    if (current && typeof stages[current].next === "function") {
      const nextStage = (stages[current].next as Function)({
        status: normalizedKey,
        ...options,
      });
      return getStage(nextStage, type);
    }
    return current
      ? getStage(Number(stages[current].order) + 1, type)
      : null;
  }
  return getStage(Number(key) + 1, type);
};
